import { useState, useEffect, memo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Av, Bdg } from '../../components/shared';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';

export const PageUserLeads = memo(() => {
  const { showToast } = useApp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const profileId = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [allLeads, setAllLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [filterSt, setFilterSt] = useState("all");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!profileId) { navigate('/team/users'); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await api.getMemberManage(profileId);
        const data = res.data || res;
        setProfile(data.profile || {});
        setAllLeads(data.leads || []);
      } catch (e) {
        showToast(e.message || "Failed to load leads", "error");
        navigate('/team/users');
      } finally { setLoading(false); }
    })();
  }, [profileId]);

  if (loading) return <div className="pg fi"><div style={{ padding: 60, textAlign: "center", color: "var(--t3)", fontSize: 14 }}>Loading leads...</div></div>;
  if (!profile) return null;

  const SC = { new: "#0891b2", contacted: "#d97706", qualified: "#059669", closed: "#64748b" };
  const SBG = { new: "rgba(8,145,178,.1)", contacted: "rgba(217,119,6,.1)", qualified: "rgba(5,150,105,.1)", closed: "rgba(100,116,139,.1)" };

  const filtered = allLeads.filter(l => {
    const name = (l.name || "").toLowerCase();
    const email = (l.email || "").toLowerCase();
    const company = (l.company || "").toLowerCase();
    const ms = name.includes(search.toLowerCase()) || email.includes(search.toLowerCase()) || company.includes(search.toLowerCase());
    return ms && (filterSt === "all" || l.status === filterSt);
  });

  const downloadCSV = () => {
    if (filtered.length === 0) { showToast("No leads to export", "error"); return; }
    const statusLabel = filterSt === "all" ? "all" : filterSt;
    const h = ["Name", "Email", "Phone", "Company", "Country", "City", "Status", "Score", "Date"];
    const csv = [h, ...filtered.map(l => [l.name, l.email, l.phone, l.company, l.country, l.city, l.status || "new", l.score || 0, l.created_at])]
      .map(r => r.map(v => `"${v || ""}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `leads_${(profile.name || "member").replace(/\s+/g, "_")}_${statusLabel}.csv`;
    a.click();
    showToast(`${filtered.length} leads exported as CSV (${statusLabel})`);
  };

  const downloadVCard = (l) => {
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${l.name || ""}`,
      `N:${(l.name || "").split(" ").reverse().join(";")};;;`,
      l.email ? `EMAIL:${l.email}` : "",
      l.phone ? `TEL:${l.phone}` : "",
      l.company ? `ORG:${l.company}` : "",
      l.job_title ? `TITLE:${l.job_title}` : "",
      l.address ? `ADR:;;${l.address};${l.city || ""};${l.country || ""};;` : (l.city || l.country ? `ADR:;;;${l.city || ""};${l.country || ""};;` : ""),
      "END:VCARD",
    ].filter(Boolean).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([vcard], { type: "text/vcard" }));
    a.download = `${(l.name || "contact").replace(/\s+/g, "_")}.vcf`;
    a.click();
    showToast(`${l.name || "Contact"} vCard downloaded`);
  };

  const cardSt = {
    background: "rgba(255,255,255,.72)",
    border: "1px solid rgba(255,255,255,.9)",
    borderRadius: 16,
    boxShadow: "0 4px 16px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
  };

  return (
    <div className="pg fi">
      {/* ── TOP NAV ── */}
      <div style={{
        display: "flex", alignItems: "center",
        background: "rgba(255,255,255,.72)",
        backdropFilter: "blur(40px)",
        border: "1px solid rgba(255,255,255,.85)",
        borderRadius: 16,
        boxShadow: "0 2px 16px rgba(0,0,0,.06)",
        marginBottom: 0,
      }}>
        <button onClick={() => navigate('/team/users')} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "14px 20px", border: "none",
          background: "transparent", cursor: "pointer",
          color: "#64748b", fontSize: 13, fontFamily: "var(--f)",
          borderRight: "1px solid rgba(0,0,0,.06)",
          transition: "all .15s", flexShrink: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = "#0f172a"}
          onMouseLeave={e => e.currentTarget.style.color = "#64748b"}>
          <span style={{ fontSize: 16 }}>←</span> Members
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "10px 22px", flex: 1 }}>
          {profile.photo ? (
            <img src={profile.photo} alt={profile.name} style={{ width: 38, height: 38, borderRadius: 11, objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <Av name={profile.name ? profile.name.slice(0, 2).toUpperCase() : "??"} grad="linear-gradient(150deg,#64748b,#059669)" size={38} />
          )}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{profile.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{allLeads.length} leads generated</div>
          </div>
        </div>

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 4, background: "rgba(0,0,0,.04)", padding: 4, borderRadius: 10, marginRight: 14 }}>
          {["all", "new", "contacted", "qualified", "closed"].map(s => (
            <button key={s} onClick={() => setFilterSt(s)} style={{
              padding: "5px 13px", borderRadius: 7, border: "none", cursor: "pointer",
              fontSize: 11.5, fontWeight: filterSt === s ? 680 : 450, fontFamily: "var(--f)",
              background: filterSt === s ? "rgba(255,255,255,.96)" : "transparent",
              color: filterSt === s ? "#0f172a" : "#94a3b8",
              boxShadow: filterSt === s ? "0 2px 8px rgba(0,0,0,.07)" : "none",
              transition: "all .15s", textTransform: "capitalize",
            }}>{s}</button>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginRight: 12 }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", pointerEvents: "none" }}>🔍</span>
          <input placeholder="Search leads…" value={search} onChange={e => setSearch(e.target.value)} style={{
            width: 200, padding: "8px 12px 8px 30px",
            background: "rgba(255,255,255,.8)", border: "1px solid rgba(0,0,0,.08)",
            borderRadius: 9, color: "#0f172a", fontSize: 12,
            fontFamily: "var(--f)", outline: "none",
          }} />
        </div>

        <button onClick={downloadCSV} style={{
          display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", marginRight: 20,
          borderRadius: 10, border: "1px solid rgba(0,0,0,.1)",
          background: "rgba(255,255,255,.8)", cursor: "pointer",
          fontSize: 12, fontWeight: 650, color: "#475569", fontFamily: "var(--f)",
          transition: "all .15s", flexShrink: 0,
          boxShadow: "0 2px 8px rgba(0,0,0,.06)",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#0f172a"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.8)"; e.currentTarget.style.color = "#475569"; }}>
          ⬇ Export CSV
        </button>
      </div>

      {/* ── STAT BAR ── */}
      <div style={{
        display: "flex", gap: 0,
        background: "rgba(255,255,255,.55)",
        border: "1px solid rgba(255,255,255,.8)",
        borderRadius: 16,
        marginTop: 16,
        marginBottom: 24,
      }}>
        {[
          { ico: "🎯", lbl: "Total Leads", val: allLeads.length, c: "#0891b2" },
          { ico: "⭐", lbl: "Qualified", val: allLeads.filter(l => l.status === "qualified").length, c: "#059669" },
          { ico: "📧", lbl: "Contacted", val: allLeads.filter(l => l.status === "contacted").length, c: "#d97706" },
          { ico: "🆕", lbl: "New", val: allLeads.filter(l => l.status === "new").length, c: "#0891b2" },
          { ico: "📊", lbl: "Showing", val: filtered.length + " results", c: "#64748b" },
        ].map(({ ico, lbl, val, c }, i) => (
          <div key={lbl} style={{
            flex: 1, padding: "13px 20px",
            borderRight: i < 4 ? "1px solid rgba(0,0,0,.05)" : "none",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: `${c}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{ico}</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 780, color: c, letterSpacing: "-.5px", lineHeight: 1 }}>{val}</div>
              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 3, fontWeight: 550 }}>{lbl}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── LEAD CARDS GRID ── */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0" }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>🎯</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#64748b" }}>No leads found</div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 8 }}>Try adjusting your search or filter</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(360px,1fr))", gap: 14 }}>
          {filtered.map((l, idx) => {
            const sc = SC[l.status] || "#64748b";
            const sbg = SBG[l.status] || "rgba(100,116,139,.1)";
            const isExp = expanded === l.id;
            const initials = (l.name || "??").split(" ").map(n => n[0]).join("").slice(0, 2);
            const dateStr = l.created_at ? new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "—";

            return (
              <div key={l.id || idx}
                onClick={() => setExpanded(isExp ? null : l.id)}
                style={{
                  ...cardSt,
                  borderColor: isExp ? sc + "44" : "rgba(255,255,255,.9)",
                  cursor: "pointer", transition: "all .2s",
                  boxShadow: isExp ? `0 8px 28px ${sc}20,inset 0 1px 0 rgba(255,255,255,.95)` : "0 4px 16px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
                  overflow: "hidden",
                }}
                onMouseEnter={e => { if (!isExp) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.1),inset 0 1px 0 rgba(255,255,255,.95)"; } }}
                onMouseLeave={e => { if (!isExp) { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)"; } }}
              >
                {/* Top color stripe */}
                <div style={{ height: 3, background: `linear-gradient(90deg,${sc},${sc}44)` }} />

                {/* Card header */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px 12px" }}>
                  {l.photo ? (
                    <img src={l.photo} alt={l.name} style={{ width: 44, height: 44, borderRadius: 13, objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 44, height: 44, borderRadius: 13, flexShrink: 0,
                      background: `linear-gradient(135deg,${sc},${sc}88)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, fontWeight: 800, color: "#fff",
                      boxShadow: `0 4px 12px ${sc}33`,
                    }}>{initials}</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 720, color: "#0f172a", letterSpacing: "-.2px" }}>{l.name || "Unknown"}</div>
                    <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.company || "—"}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: sbg, color: sc, textTransform: "capitalize", border: `1px solid ${sc}22` }}>{l.status || "new"}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: "#7c3aed" }}>✦ {l.score || 0}</span>
                  </div>
                </div>

                {/* Info row */}
                <div style={{ display: "flex", padding: "0 18px 14px", gap: 12, flexWrap: "wrap" }}>
                  {[
                    { ico: "📧", val: l.email },
                    { ico: "🏙", val: l.city || l.country },
                    { ico: "📅", val: dateStr },
                  ].filter(x => x.val).map(({ ico, val }, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                      <span style={{ fontSize: 11, flexShrink: 0 }}>{ico}</span>
                      <span style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val}</span>
                    </div>
                  ))}
                  <div style={{ marginLeft: "auto", fontSize: 12, color: "#cbd5e1", transition: "transform .2s", transform: isExp ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}>›</div>
                </div>

                {/* Expanded */}
                {isExp && (
                  <div style={{ padding: "14px 18px 18px", borderTop: "1px solid rgba(0,0,0,.06)", background: "rgba(248,250,252,.6)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 13 }}>
                      {[
                        { ico: "📞", lbl: "Phone", val: l.phone },
                        { ico: "🌍", lbl: "Country", val: l.country },
                        { ico: "🏢", lbl: "Company", val: l.company },
                      ].map(({ ico, lbl, val }) => (
                        <div key={lbl} style={{ padding: "10px 11px", borderRadius: 10, background: "rgba(255,255,255,.9)", border: "1px solid rgba(0,0,0,.06)" }}>
                          <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>{ico} {lbl}</div>
                          <div style={{ fontSize: 11.5, color: "#0f172a", fontWeight: 570, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val || "—"}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8 }} onClick={e => e.stopPropagation()}>
                      {l.email && <a href={`mailto:${l.email}`} className="btn bp" style={{ flex: 1, justifyContent: "center", fontSize: 12, gap: 5, textDecoration: "none" }}>📧 Email</a>}
                      {l.phone && <a href={`tel:${l.phone}`} className="btn bs" style={{ flex: 1, justifyContent: "center", fontSize: 12, gap: 5, textDecoration: "none" }}>📞 Call</a>}
                      <button className="btn bs" style={{ flex: 1, justifyContent: "center", fontSize: 12, gap: 5, background: "rgba(124,58,237,.08)", border: "1px solid rgba(124,58,237,.2)", color: "#7c3aed" }} onClick={() => downloadVCard(l)}>⬇ vCard</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
